import { FLIGHT_DEALS_ONLY } from "@/lib/release";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Building2, Compass, Mountain, Loader2 } from "lucide-react";
import RegistrationVerification from "./RegistrationVerification";

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordConfirmTouched, setPasswordConfirmTouched] = useState(false);
  const [accountType, setAccountType] = useState("traveller");
  const [organizationName, setOrganizationName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  useEffect(() => { sessionStorage.removeItem("trailpulse:pending-registration"); }, []);
  const passwordsMismatch = passwordConfirmTouched && password !== passwordConfirm;
  useEffect(() => {
    if (pendingRegistration && user && (
      (user.email === pendingRegistration.email && user.email_verified_at) ||
      (user.phone && user.phone === pendingRegistration.phone && user.phone_verified_at)
    )) {
      sessionStorage.removeItem("trailpulse:pending-registration");
      setPendingRegistration(null); nav("/", { replace: true });
    }
  }, [pendingRegistration, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() && !phone.trim()) { setError("Enter an email address or phone number."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== passwordConfirm) { setPasswordConfirmTouched(true); setError(""); return; }
    if (accountType === "organizer" && !organizationName.trim()) { setError("Organization name is required for organizer accounts."); return; }
    setLoading(true); setError("");
    try {
      const result = await register(fullName, email, password, phone, accountType, organizationName);
      if (result.pending_registration) {
        sessionStorage.setItem("trailpulse:pending-registration", JSON.stringify(result));
        setPendingRegistration(result); setPassword(""); setPasswordConfirm("");
      } else nav("/verify-contact");
    }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  if (pendingRegistration) return <RegistrationVerification registration={pendingRegistration} onRestart={() => { sessionStorage.removeItem("trailpulse:pending-registration"); setPendingRegistration(null); }} />;
  return (
    <div className="tp-app-shell text-[#F4EFEB] antialiased" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-8">
        <Link to="/landing" data-testid="register-home-link" className="mb-6 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#E5B85C]">
            <Mountain className="h-5 w-5 text-[#17211D]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>TrailPulse</span>
        </Link>

        <form onSubmit={submit} data-testid="register-form" className="tp-dark-panel rounded-[2rem] p-7">
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Create account</h1>
          <p className="mt-1 text-sm text-[#94A3B8]">Join TrailPulse to explore flight deals.</p>

          {/* Organizer registration is preserved for the full release. */}
          {!FLIGHT_DEALS_ONLY && <fieldset className="mt-5">
            <legend className="block text-xs font-medium text-[#94A3B8]">Account type</legend>
            <div className="mt-2 grid rounded-full border border-white/15 bg-[#FFFAF1]/10 p-1 sm:grid-cols-2">
              <button
                type="button"
                data-testid="register-account-traveller"
                aria-pressed={accountType === "traveller"}
                onClick={() => setAccountType("traveller")}
                className={`flex min-h-10 items-center justify-center gap-2 rounded-full px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] ${accountType === "traveller" ? "bg-[#E5B85C] text-[#17211D]" : "text-[#CFE7DC] hover:bg-[#FFFAF1]/10"}`}
              >
                <Compass className="h-4 w-4" /> Traveller
              </button>
              <button
                type="button"
                data-testid="register-account-organizer"
                aria-pressed={accountType === "organizer"}
                onClick={() => setAccountType("organizer")}
                className={`flex min-h-10 items-center justify-center gap-2 rounded-full px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] ${accountType === "organizer" ? "bg-[#E5B85C] text-[#17211D]" : "text-[#CFE7DC] hover:bg-[#FFFAF1]/10"}`}
              >
                <Building2 className="h-4 w-4" /> Organizer
              </button>
            </div>
          </fieldset>}

          {!FLIGHT_DEALS_ONLY && accountType === "organizer" && (
            <>
              <label htmlFor="register-org-name" className="mt-4 block text-xs font-medium text-[#94A3B8]">Organization name</label>
              <input id="register-org-name" data-testid="register-org-name" type="text" placeholder="Your company or community name" value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)} required={accountType === "organizer"}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
            </>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="register-name" className="block text-xs font-medium text-[#94A3B8]">Name</label>
              <input id="register-name" data-testid="register-name" type="text" placeholder="Aarav Khanna" value={fullName}
                onChange={(e) => setFullName(e.target.value)} required
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
            </div>
            <div>
              <label htmlFor="register-phone" className="block text-xs font-medium text-[#94A3B8]">Phone</label>
              <input id="register-phone" data-testid="register-phone" type="tel" placeholder="+91..." value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
            </div>
          </div>

          <label htmlFor="register-email" className="mt-4 block text-xs font-medium text-[#94A3B8]">Email</label>
          <input id="register-email" data-testid="register-email" type="email" placeholder="you@company.com" value={email}
            onChange={(e) => setEmail(e.target.value)} required={!phone.trim()}
            className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="register-password" className="block text-xs font-medium text-[#94A3B8]">Password</label>
              <input id="register-password" data-testid="register-password" type="password" placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
            </div>
            <div>
              <label htmlFor="register-password-confirm" className="block text-xs font-medium text-[#94A3B8]">Confirm password</label>
              <input id="register-password-confirm" data-testid="register-password-confirm" type="password" placeholder="Confirm password" value={passwordConfirm}
                onChange={(e) => { setPasswordConfirm(e.target.value); setPasswordConfirmTouched(true); }} required minLength={8}
                aria-invalid={passwordsMismatch} aria-describedby={passwordsMismatch ? "register-password-confirm-error" : undefined}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
              <div aria-live="polite">
                {passwordsMismatch && <p id="register-password-confirm-error" data-testid="register-password-confirm-error" className="mt-1.5 text-xs text-[#E5695B]">Passwords do not match.</p>}
              </div>
            </div>
          </div>

          {error && <div data-testid="register-error" className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#E5695B]">{error}</div>}

          <button data-testid="register-submit" disabled={loading} type="submit"
              className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] transition-colors hover:bg-[#f0c96f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#13221A] disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</> : "Create account"}
          </button>

          <p className="mt-5 text-center text-sm text-[#94A3B8]">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="font-semibold text-[#E5B85C] hover:text-[#f0c96f]">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
